import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { generateChatResponse } from '@/services/llmService';
import { marketDataService } from '@/services/marketData/marketDataService';

interface FinancialAnalystProps {
  selectedSymbol?: string;
}

export const FinancialAnalystChat: React.FC<FinancialAnalystProps> = ({ selectedSymbol }) => {
  const [analysis, setAnalysis] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [marketData, setMarketData] = useState<any>(null);
  const [currentSymbol, setCurrentSymbol] = useState<string | undefined>(selectedSymbol);
  const [shouldAutoAnalyze, setShouldAutoAnalyze] = useState<boolean>(false);

  // Track symbol changes and clear previous analysis
  useEffect(() => {
    if (selectedSymbol !== currentSymbol) {
      setCurrentSymbol(selectedSymbol);
      // Clear previous analysis when symbol changes
      setAnalysis('');
      setError(null);
      // Reset market data
      setMarketData(null);
      
      // Auto-analyze for popular symbols
      const popularSymbols = ['SPY', 'AAPL', 'MSFT', 'GOOGL', 'TSLA', 'AMZN', 'META'];
      if (selectedSymbol && popularSymbols.includes(selectedSymbol)) {
        setShouldAutoAnalyze(true);
      }
    }
  }, [selectedSymbol, currentSymbol]);

  // Fetch market data for the selected symbol
  useEffect(() => {
    if (currentSymbol) {
      const fetchMarketData = async () => {
        try {
          // Get quote and historical data from Alpaca
          const quoteData = await marketDataService.getQuote(currentSymbol, 'alpaca');
          const historicalData = await marketDataService.getTimeSeriesDaily(currentSymbol, 'alpaca');
          
          if (quoteData && historicalData && historicalData.length > 0) {
            // Calculate some basic metrics
            const currentPrice = quoteData.price;
            
            // Get price changes
            const priceYesterday = historicalData[1]?.close || historicalData[1]?.c;
            const priceChange = priceYesterday ? currentPrice - priceYesterday : 0;
            const priceChangePercent = priceYesterday ? (priceChange / priceYesterday) * 100 : 0;
            
            // Get 30-day high/low
            const recentData = historicalData.slice(0, 30);
            const high30d = Math.max(...recentData.map(bar => parseFloat(bar.high || bar.h)));
            const low30d = Math.min(...recentData.map(bar => parseFloat(bar.low || bar.l)));
            
            setMarketData({
              currentPrice,
              priceChange,
              priceChangePercent,
              high30d,
              low30d,
              volume: quoteData.size || 0,
              timestamp: quoteData.timestamp
            });
            
            // If auto analyze is set, run analysis automatically
            if (shouldAutoAnalyze) {
              setShouldAutoAnalyze(false);
              setTimeout(() => {
                runAnalysis();
              }, 500); // Small delay to ensure market data is properly set
            }
          }
        } catch (err) {
          console.error('Error fetching market data for analyst:', err);
          // Don't set error state here, just log - we can still proceed with analysis
        }
      };
      
      fetchMarketData();
    }
  }, [currentSymbol, shouldAutoAnalyze]);

  const runAnalysis = async () => {
    if (!currentSymbol) return;
    setLoading(true);
    setError(null);
    try {
      let promptEnhancements = '';
      
      // Add real-time data to the prompt if available
      if (marketData) {
        promptEnhancements = `
Current Price: $${marketData.currentPrice.toFixed(2)}
Price Change: ${marketData.priceChange >= 0 ? '+' : ''}${marketData.priceChange.toFixed(2)} (${marketData.priceChangePercent.toFixed(2)}%)
30-Day Range: $${marketData.low30d.toFixed(2)} - $${marketData.high30d.toFixed(2)}
Last Updated: ${new Date(marketData.timestamp).toLocaleString()}
`;
      }
      
      const prompt = `Provide a concise Buy, Sell, or Hold recommendation with rationale for the stock symbol ${currentSymbol}.
${promptEnhancements}
Base your analysis on the current market data provided above along with your knowledge of market trends, sector performance, and company fundamentals. Include key metrics that influenced your decision.`;
      
      const content = await generateChatResponse(prompt, []);
      setAnalysis(content);
    } catch (err: any) {
      console.error('Error generating analysis:', err);
      setError(err.message || 'Failed to generate analysis');
    } finally {
      setLoading(false);
    }
  };

  if (!currentSymbol) {
    return <p>Please select a symbol for analysis</p>;
  }

  return (
    <div className="space-y-4">
      <Button onClick={runAnalysis} disabled={loading} className="w-full">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (analysis ? 'Re-run Analysis' : 'Run Analysis')}
      </Button>
      {error && <div className="text-red-500">{error}</div>}
      {marketData && !analysis && !loading && (
        <div className="text-xs text-muted-foreground">
          Analysis will use real-time data from Alpaca
        </div>
      )}
      {analysis && (
        <div className="prose prose-sm max-w-full">
          {analysis.split(/\n+/).map((paragraph, idx) => (
            <p key={idx}>{paragraph}</p>
          ))}
        </div>
      )}
    </div>
  );
};
